import * as React from 'react';
import moment from 'moment-timezone';
import { FORMATS } from '@lib/common';
import { inject, observer } from 'mobx-react';
import { Button, Modal, ModalContent, ModalTitle } from '@lib/components';
import { MobxComponent } from '../../mobx/components';

/**
 * Shown when a save was refused because someone else changed the menu first.
 *
 * Mounted once at the root rather than at each save site. Every menu save in
 * the original dashboard funnels through `api.restaurant_update`, which raises
 * the conflict on the store; this renders whatever is outstanding.
 *
 * Neither button is safe by default, so both say what they do. Reload throws
 * away unsaved edits despite its reassuring label. Overwrite is deliberately
 * still offered: version history keeps both states, which is what makes it a
 * recoverable choice rather than a gamble.
 */
@inject('store')
@observer
export class MenuConflictDialog extends MobxComponent<{}, {}> {
	render() {
		const { menu_conflict } = this.injected.store;
		const conflict = menu_conflict.active;
		if (!conflict) return null;

		const who = describeAuthor(conflict.last_updated_by);
		const when = describeWhen(conflict.last_updated_by?.at);

		return (
			<Modal active width={440} close={() => menu_conflict.dismiss()}>
				<ModalTitle className="round-top-sm">
					<h3 className="text-center flex-grow">Someone else changed this menu</h3>
				</ModalTitle>
				<ModalContent paddingtb={24} paddinglr={24}>
					<p style={{ marginBottom: 8 }}>
						{who} {when}. Your changes have not been saved.
					</p>
					<p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
						Taking theirs discards the edits you have not saved. Keeping yours replaces theirs.
						Both versions are kept in version history, so either choice can be undone. Close this
						to decide later — your edits stay in the form, unsaved.
					</p>
					<div style={{ display: 'flex', gap: 10 }}>
						<Button full disabled={menu_conflict.working} onClick={() => menu_conflict.reload()}>
							{menu_conflict.working ? 'Discarding...' : 'Take theirs'}
						</Button>
						{conflict.overwrite && (
							<Button
								full
								color="primary"
								disabled={menu_conflict.working}
								onClick={() => menu_conflict.saveMine()}
							>
								{menu_conflict.working ? 'Saving...' : 'Keep mine'}
							</Button>
						)}
					</div>
				</ModalContent>
			</Modal>
		);
	}
}

/** Names the person who saved first, falling back as identity thins out. */
function describeAuthor(author?: T.API.MenuAuthorStamp): string {
	if (!author) return 'Someone else saved changes';
	if (author.name) return `${author.name} saved changes`;
	if (author.email) return `${author.email} saved changes`;
	return 'Someone else saved changes';
}

/**
 * "2 minutes ago" reads better than a timestamp for something that just
 * happened, which is the only case this dialog appears in. Past a day it falls
 * back to the timestamp, since "8 days ago" is not a conflict worth relating.
 */
function describeWhen(at?: string | Date): string {
	const when = at ? moment(at) : null;
	if (!when?.isValid()) return 'a moment ago';
	if (moment().diff(when, 'hours') >= 24) return when.format(FORMATS.moment.localized.datetime);
	return when.fromNow();
}
